// src/contexts/LiveWorkoutContext.jsx
//
// Estado GLOBAL da sessão de treino ao vivo (keystone da Phase D-evo).
// O TIMER roda AQUI (não no ModoTreino) → sobrevive à navegação/minimização.
// Guarda: workout ativo, sessao_id, startedAt, exercícios+séries, métricas ao
// vivo (com TETO no previsto), flag aberto/minimizado, e o request de check-in
// (instância única de RegistrarTreinoCheckin vive no root — Parte I).
//
// WORKOUT_LOG continua DESACOPLADO do XP: cargas vão pro log (workoutLogService);
// XP ao vivo é só display (servidor é a fonte da verdade no check-in).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  iniciarSessao,
  registrarSerie,
  finalizarSessao,
} from "../features/treinos/services/workoutLogService.js";
import {
  calculateCompletedWorkoutXp,
  buildPredictedAtividade,
} from "../features/treinos/utils/workoutXp.js";
import { getWorkoutCaloriesEstimate } from "../features/treinos/components/muscle-map/muscleMapUtils.js";
import { libToExercicio } from "../features/treinos/components/treino-screens/exerciseLibraryService.js";

const LiveWorkoutContext = createContext(null);

// Contexto SEPARADO só p/ o cronômetro (elapsed/running): muda a cada 1s. Manter
// isto fora do value principal evita que TODO consumidor (TreinoDetalhes, dashboard,
// offcanvas de evolução…) re-renderize por segundo — o que fazia a tela "piscar" e
// atrapalhava editar a carga. Só quem mostra o tempo assina este contexto.
const LiveWorkoutTimerContext = createContext({ elapsed: 0, running: false });

// ── Helpers ────────────────────────────────────────────────────────────────

// Só o número das reps planejadas ("10", "8-12" → 10) pra pré-preencher a linha.
function planRepsNumber(reps) {
  if (reps == null) return "";
  const m = String(reps).match(/\d+/);
  return m ? m[0] : "";
}

// Normaliza um exercício (plano OU biblioteca) p/ o shape da sessão ao vivo,
// já com as LINHAS da tabela pré-preenchidas (dim) a partir do planejado.
function normalizeEx(ex, i) {
  const planSeries = Number(ex.ALVO?.series ?? ex.series ?? ex.SERIES) || 0;
  const reps = ex.ALVO?.reps ?? ex.reps ?? ex.REPS ?? null;
  const descanso = ex.ALVO?.descanso ?? ex.descanso ?? ex.DESCANSO ?? null;
  const rpe = ex.ALVO?.rpe ?? ex.rpe ?? ex.RPE ?? null;
  const nRows = planSeries > 0 ? planSeries : 3; // fallback: 3 linhas

  const PLAN = { series: planSeries, reps, descanso, rpe };
  const rows = Array.from({ length: nRows }, () => ({
    reps: planRepsNumber(reps),
    carga: "",
    descanso: descanso != null ? String(descanso) : "",
    rpe: rpe != null ? String(rpe) : "",
    done: false,
    extra: false, // linha além do planejado (conta como trabalho extra)
  }));

  return {
    _id: ex.EXERCISE_ID || ex.nome || ex.NOME || `ex_${i}`,
    EXERCISE_ID: ex.EXERCISE_ID || null,
    NOME: ex.NOME || ex.nome || "",
    MUSCLE_GROUP: ex.MUSCLE_GROUP || ex.muscle_group || ex.GRUPO_MUSCULAR || null,
    SECONDARY_MUSCLE_GROUPS: Array.isArray(ex.SECONDARY_MUSCLE_GROUPS)
      ? ex.SECONDARY_MUSCLE_GROUPS
      : Array.isArray(ex.secondary_muscle_groups)
      ? ex.secondary_muscle_groups
      : [],
    URL_VIDEO: ex.URL_VIDEO || ex.video_url || null,
    URL_VIDEO_2: ex.URL_VIDEO_2 || ex.video_url_2 || null,
    PLAN,
    rows,
  };
}

// Linhas concluídas → SERIES (shape do workoutLogService / métricas).
function rowsToSeries(rows) {
  const out = [];
  rows.forEach((r, idx) => {
    if (!r.done) return;
    out.push({
      NUMERO: out.length + 1,
      CARGA: Number(r.carga) || 0,
      REPS: Number(r.reps) || 0,
      RPE: r.rpe !== "" && r.rpe != null ? Number(r.rpe) : null,
      _rowIdx: idx,
    });
  });
  return out;
}

export function LiveWorkoutProvider({ children }) {
  // ── Sessão ────────────────────────────────────────────────────────────────
  const [session, setSession] = useState(null);
  // session = { workoutFull, raw, sessaoId, nomeTreino, diaTreino, origemTreino }
  const [exState, setExState] = useState([]);
  const [open, setOpen] = useState(false);     // ModoTreino visível (full)
  const [phase, setPhase] = useState("idle");  // idle | active | finished | abandoned
  const [summaryStatus, setSummaryStatus] = useState(null); // CONCLUIDO | ABANDONADO
  const [totais, setTotais] = useState(null);

  const uidRef = useRef(null);
  const setUid = useCallback((uid) => { uidRef.current = uid || null; }, []);

  // ── Timer (roda no provider) ───────────────────────────────────────────────
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const accRef = useRef(0);        // segundos acumulados (antes da pausa atual)
  const segStartRef = useRef(null); // epoch ms do início do segmento atual
  const tickRef = useRef(null);

  const computeElapsed = useCallback(() => {
    const base = accRef.current;
    if (segStartRef.current != null) {
      return base + Math.floor((Date.now() - segStartRef.current) / 1000);
    }
    return base;
  }, []);

  const startTimer = useCallback(() => {
    if (segStartRef.current != null) return; // já rodando
    segStartRef.current = Date.now();
    setRunning(true);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => setElapsed(computeElapsed()), 1000);
    setElapsed(computeElapsed());
  }, [computeElapsed]);

  const pauseTimer = useCallback(() => {
    if (segStartRef.current == null) return;
    accRef.current = computeElapsed();
    segStartRef.current = null;
    setRunning(false);
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setElapsed(accRef.current);
  }, [computeElapsed]);

  const resumeTimer = useCallback(() => { startTimer(); }, [startTimer]);

  const resetTimer = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    accRef.current = 0;
    segStartRef.current = null;
    setRunning(false);
    setElapsed(0);
  }, []);

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  // ── Métricas ao vivo (display, com TETO no previsto) ───────────────────────
  const metrics = useMemo(() => {
    const raw = session?.raw || null;

    let completedSets = 0;
    let plannedSets = 0;
    let volumeTotal = 0;
    exState.forEach((ex) => {
      plannedSets += ex.PLAN?.series || ex.rows.length || 0;
      ex.rows.forEach((r) => {
        if (r.done) {
          completedSets += 1;
          volumeTotal += (Number(r.carga) || 0) * (Number(r.reps) || 0);
        }
      });
    });

    const exDone = exState.filter((ex) => ex.rows.some((r) => r.done)).length;
    const exTotal = exState.length;

    // ratio de trabalho: passa de 1 só com séries/exercícios ALÉM do planejado.
    const ratioByExercise = exTotal > 0 ? exDone / exTotal : 0;
    const workRatio = plannedSets > 0 ? completedSets / plannedSets : ratioByExercise;

    // Calorias previstas (plano) — TETO = previsto; escala com workRatio.
    const predictedCal = raw ? getWorkoutCaloriesEstimate(raw) : 0;
    const caloriasLive = Math.round(predictedCal * workRatio);

    // XP de display: sessaoId no objeto → liveBonus ativa (Parte F). Escala c/ ratio.
    const xpInfo = raw
      ? calculateCompletedWorkoutXp({ workout: raw, sessaoId: session?.sessaoId || "live" })
      : { base: 0, liveBonus: 0, total: 0 };
    const xpLive = Math.round(xpInfo.total * workRatio);

    return {
      completedSets,
      plannedSets,
      volumeTotal,
      exDone,
      exTotal,
      progresso: exTotal > 0 ? Math.min(1, exDone / exTotal) : 0,
      workRatio,
      caloriasLive,
      xpInfo,
      xpLive,
      predictedCal,
    };
  }, [exState, session]);

  // Exercício "atual" = primeiro sem série concluída.
  const currentEx = useMemo(() => {
    const firstOpen = exState.find((e) => !e.rows.some((r) => r.done));
    return firstOpen || exState[exState.length - 1] || null;
  }, [exState]);

  // ── API ─────────────────────────────────────────────────────────────────
  const startSession = useCallback((workout) => {
    if (!workout) return;
    const raw = workout.raw || workout;
    const exercicios = workout.principais || workout.exercicios || [];
    const normalized = exercicios.map(normalizeEx);

    const nomeTreino = workout.nome || workout.NOME || raw?.NOME_TREINO || "Treino";
    const diaTreino = workout.dia || raw?.DIA || "";
    const origemTreino = workout.origemTreino || "PLANO_COMPLETO_IA";

    setExState(normalized);
    setSession({ workoutFull: workout, raw, sessaoId: null, nomeTreino, diaTreino, origemTreino });
    setPhase("active");
    setSummaryStatus(null);
    setTotais(null);
    setOpen(true);

    // Auto-start do cronômetro (Parte C).
    resetTimer();
    startTimer();

    // Cria a sessão no WORKOUT_LOG.
    const uid = uidRef.current;
    if (uid) {
      iniciarSessao({ uid, origemTreino, diaTreino, nomeTreino, exercicios: normalized.map((ex) => ({
        EXERCISE_ID: ex.EXERCISE_ID,
        NOME: ex.NOME,
        MUSCLE_GROUP: ex.MUSCLE_GROUP,
        SECONDARY_MUSCLE_GROUPS: ex.SECONDARY_MUSCLE_GROUPS,
        ALVO: { series: ex.PLAN.series, reps: ex.PLAN.reps, descanso: ex.PLAN.descanso },
      })) })
        .then((id) => setSession((s) => (s ? { ...s, sessaoId: id } : s)))
        .catch(console.error);
    }
  }, [resetTimer, startTimer]);

  const openSession = useCallback(() => setOpen(true), []);
  const minimizeSession = useCallback(() => setOpen(false), []);

  // Edita um campo de uma linha (reps/carga/descanso/rpe) — não conclui.
  const updateRow = useCallback((exId, rowIdx, field, value) => {
    setExState((prev) => prev.map((ex) => {
      if (ex._id !== exId) return ex;
      const rows = ex.rows.map((r, i) => (i === rowIdx ? { ...r, [field]: value } : r));
      return { ...ex, rows };
    }));
  }, []);

  // Conclui/registra uma série (tap no STATUS). Acende a linha + persiste no log.
  const registerSet = useCallback((exId, rowIdx) => {
    let persistPayload = null;
    setExState((prev) => prev.map((ex) => {
      if (ex._id !== exId) return ex;
      const rows = ex.rows.map((r, i) => (i === rowIdx ? { ...r, done: !r.done } : r));
      const row = rows[rowIdx];
      if (row.done) {
        const numero = rows.slice(0, rowIdx + 1).filter((r) => r.done).length;
        persistPayload = {
          exerciseId: ex._id,
          serie: {
            NUMERO: numero,
            CARGA: Number(row.carga) || 0,
            REPS: Number(row.reps) || 0,
            RPE: row.rpe !== "" && row.rpe != null ? Number(row.rpe) : null,
            FEITA_AT: new Date().toISOString(),
          },
        };
      }
      return { ...ex, rows };
    }));

    // Fire-and-forget no WORKOUT_LOG (só quando concluiu, não ao desfazer).
    const uid = uidRef.current;
    const sessaoId = session?.sessaoId;
    if (persistPayload && uid && sessaoId) {
      registrarSerie({ uid, sessaoId, ...persistPayload }).catch(console.error);
    }
  }, [session]);

  // Adiciona uma linha ALÉM do planejado (trabalho extra → métrica passa do teto).
  const addSet = useCallback((exId) => {
    setExState((prev) => prev.map((ex) => {
      if (ex._id !== exId) return ex;
      const last = ex.rows[ex.rows.length - 1];
      return {
        ...ex,
        rows: [...ex.rows, {
          reps: last?.reps ?? planRepsNumber(ex.PLAN.reps),
          carga: last?.carga ?? "",
          descanso: ex.PLAN.descanso != null ? String(ex.PLAN.descanso) : "",
          rpe: ex.PLAN.rpe != null ? String(ex.PLAN.rpe) : "",
          done: false,
          extra: true,
        }],
      };
    }));
  }, []);

  const addExercise = useCallback((libRow) => {
    setExState((prev) => {
      const novo = normalizeEx(
        libToExercicio(libRow, { series: 3, reps: "10", descanso: "60" }),
        prev.length,
      );
      return [...prev, novo];
    });
  }, []);

  const finalizeInternal = useCallback(async (status) => {
    pauseTimer();
    const uid = uidRef.current;
    const sessaoId = session?.sessaoId;
    let result = null;
    if (uid && sessaoId) {
      try {
        result = await finalizarSessao({ uid, sessaoId, status });
      } catch (err) {
        console.error(err);
      }
    }
    if (!result) {
      // fallback local
      let totalSeries = 0; let tonelagemTotal = 0;
      exState.forEach((ex) => ex.rows.forEach((r) => {
        if (r.done) { totalSeries++; tonelagemTotal += (Number(r.carga) || 0) * (Number(r.reps) || 0); }
      }));
      result = { totalSeries, tonelagemTotal };
    }
    setTotais(result);
    setSummaryStatus(status);
    setPhase(status === "CONCLUIDO" ? "finished" : "abandoned");
    setOpen(true); // mostra a tela de resumo dentro do shell
    if (status === "CONCLUIDO") {
      // Momento de valor: treino salvo. É AQUI (não no boot) que o app pede a
      // permissão de notificação, se ainda não pediu (ver PUSH_PROMPT).
      import("../services/notifications/FCMService")
        .then(({ FCMService }) => FCMService.valueMoment("treino_salvo"))
        .catch(() => {});
    }
    return result;
  }, [pauseTimer, session, exState]);

  const finishSession = useCallback(() => finalizeInternal("CONCLUIDO"), [finalizeInternal]);
  const abandonSession = useCallback(() => finalizeInternal("ABANDONADO"), [finalizeInternal]);

  // Encerra de vez (fecha shell + limpa estado).
  const closeSession = useCallback(() => {
    setOpen(false);
    setSession(null);
    setExState([]);
    setPhase("idle");
    setSummaryStatus(null);
    setTotais(null);
    resetTimer();
  }, [resetTimer]);

  // ── Check-in (instância única no root) ─────────────────────────────────────
  const [checkin, setCheckin] = useState({
    liveTrigger: 0, livePrefill: null,
    manualTrigger: 0,
    editingCheckin: null,
  });
  const [savedCounter, setSavedCounter] = useState(0);

  // Abre o check-in. { live, prefill, editingCheckin }
  const openCheckin = useCallback(({ live = false, prefill = null, photo = null, editingCheckin = null } = {}) => {
    if (editingCheckin) {
      setCheckin((c) => ({ ...c, editingCheckin }));
      return;
    }
    if (live) {
      setCheckin((c) => ({ ...c, livePrefill: prefill, liveTrigger: c.liveTrigger + 1 }));
    } else {
      setCheckin((c) => ({ ...c, photo, manualTrigger: c.manualTrigger + 1 }));
    }
  }, []);

  const clearEditingCheckin = useCallback(() => {
    setCheckin((c) => ({ ...c, editingCheckin: null }));
  }, []);

  const markSaved = useCallback(() => setSavedCounter((v) => v + 1), []);

  // Finalizar (CONCLUÍDO) → abre check-in pré-preenchido AO VIVO (Parte I).
  const startLiveCheckin = useCallback(() => {
    const w = session?.workoutFull || session?.raw || null;
    const atividades = w ? buildPredictedAtividade(session?.raw || w) : [];
    const mins = Math.round(computeElapsed() / 60);
    // Séries / volume / exercícios ESTRUTURADOS na atividade. O card de
    // compartilhamento (treinos/utils/workoutMetrics.js) lê campo, nunca
    // parseia o description. Só a sessão ao vivo tem esses dados — o check-in
    // manual não grava (e o card fica com duração + kcal, por regra).
    if (atividades[0]) {
      atividades[0] = {
        ...atividades[0],
        series_total: Number(totais?.totalSeries ?? metrics.completedSets) || 0,
        volume_kg: Math.round(Number(totais?.tonelagemTotal ?? metrics.volumeTotal) || 0),
        exercises_count: Number(metrics.exDone) || 0,
      };
    }
    const desc = [
      totais?.totalSeries ? `${totais.totalSeries} séries` : null,
      metrics.volumeTotal ? `${metrics.volumeTotal.toLocaleString("pt-BR")} kg de volume` : null,
      mins ? `${mins} min` : null,
    ].filter(Boolean).join(" · ");

    openCheckin({
      live: true,
      prefill: { title: session?.nomeTreino || "Treino", atividades, description: desc },
    });
    closeSession();
  }, [session, totais, metrics, computeElapsed, openCheckin, closeSession]);

  const value = useMemo(() => ({
    // estado
    session,
    exState,
    open,
    phase,
    summaryStatus,
    totais,
    metrics,
    currentEx,
    hasActiveSession: phase === "active",
    isBusy: phase !== "idle", // ativa OU em resumo → bloqueia iniciar outro treino
    // identidade
    setUid,
    // controle de sessão
    startSession,
    openSession,
    minimizeSession,
    finishSession,
    abandonSession,
    closeSession,
    pauseTimer,
    resumeTimer,
    // séries / exercícios
    updateRow,
    registerSet,
    addSet,
    addExercise,
    // check-in
    checkin,
    savedCounter,
    openCheckin,
    clearEditingCheckin,
    markSaved,
    startLiveCheckin,
  }), [
    session, exState, open, phase, summaryStatus, totais,
    metrics, currentEx, setUid, startSession, openSession, minimizeSession,
    finishSession, abandonSession, closeSession, pauseTimer, resumeTimer,
    updateRow, registerSet, addSet, addExercise, checkin, savedCounter,
    openCheckin, clearEditingCheckin, markSaved, startLiveCheckin,
  ]);

  // Value isolado do cronômetro — re-renderiza por segundo só quem o assina.
  const timerValue = useMemo(() => ({ elapsed, running }), [elapsed, running]);

  return (
    <LiveWorkoutContext.Provider value={value}>
      <LiveWorkoutTimerContext.Provider value={timerValue}>
        {children}
      </LiveWorkoutTimerContext.Provider>
    </LiveWorkoutContext.Provider>
  );
}

export function useLiveWorkout() {
  const ctx = useContext(LiveWorkoutContext);
  if (!ctx) throw new Error("useLiveWorkout deve ser usado dentro de LiveWorkoutProvider");
  return ctx;
}

// Cronômetro (elapsed/running) — assine SÓ onde o tempo é exibido. Separado p/ não
// re-renderizar a árvore inteira a cada segundo (ver LiveWorkoutTimerContext).
export function useLiveWorkoutTimer() {
  return useContext(LiveWorkoutTimerContext);
}

export default LiveWorkoutContext;
